import { Component, inject } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { finalize } from 'rxjs';

import { AuthService } from '../../../core/auth/auth.service';
import { TenantBrandingService } from '../../../core/branding/tenant-branding.service';
import { TranslatePipe } from '../../../core/i18n/translate.pipe';

@Component({
  selector: 'app-register-verify-page',
  standalone: true,
  imports: [RouterLink, TranslatePipe],
  template: `
    <div class="verify-page" [style.--tenant-primary]="tenantPrimaryColor" [style.--tenant-secondary]="tenantSecondaryColor" [style.--tenant-accent]="tenantAccentColor">
      <div class="verify-page__card">
        @if (brandingLogoUrl) {
          <img [src]="brandingLogoUrl" alt="Tenant logo" class="verify-page__logo" />
        }
        <h1 class="verify-page__title">{{ tenantDisplayName || ('auth.verifyEmailTitle' | t) }}</h1>

        @if (isLoading) {
          <p class="verify-page__meta">{{ 'auth.verifyEmailLoading' | t }}</p>
        } @else if (isVerified) {
          <p class="verify-page__success">{{ 'auth.verifyEmailSuccess' | t }}</p>
          <a [routerLink]="['/', tenantSlug, 'login']">{{ 'auth.backToLogin' | t }}</a>
        } @else {
          <p class="verify-page__error">{{ errorText | t }}</p>
          <a [routerLink]="['/', tenantSlug, 'register']">{{ 'auth.register' | t }}</a>
        }
      </div>
    </div>
  `,
  styles: [`
    .verify-page {
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      background:
        radial-gradient(circle at top left, color-mix(in srgb, var(--tenant-primary, #2563eb) 18%, transparent), transparent 34%),
        linear-gradient(160deg, color-mix(in srgb, var(--tenant-secondary, #0f172a) 7%, white), #f8fafc 55%);
      padding: 24px;
      box-sizing: border-box;
    }

    .verify-page__card {
      width: 100%;
      max-width: 460px;
      background: #ffffff;
      border-radius: 16px;
      padding: 28px;
      box-sizing: border-box;
      box-shadow: 0 18px 40px color-mix(in srgb, var(--tenant-secondary, #0f172a) 16%, transparent);
      display: grid;
      gap: 12px;
    }

    .verify-page__logo {
      width: auto;
      max-width: 180px;
      max-height: 52px;
      display: block;
      margin-bottom: 4px;
      object-fit: contain;
    }

    .verify-page__title {
      margin: 0;
      font-size: 24px;
      color: var(--tenant-secondary, #111827);
    }

    .verify-page__meta {
      margin: 0;
      color: #374151;
    }

    .verify-page__success {
      margin: 0;
      color: #166534;
    }

    .verify-page__error {
      margin: 0;
      color: #b91c1c;
    }
  `],
})
export class RegisterVerifyPageComponent {
  private readonly authService = inject(AuthService);
  private readonly route = inject(ActivatedRoute);
  private readonly tenantBranding = inject(TenantBrandingService);

  protected readonly tenantSlug = this.route.snapshot.paramMap.get('companySlug')?.trim().toLowerCase() ?? '';
  protected readonly token = this.route.snapshot.queryParamMap.get('token')?.trim() ?? '';

  protected isLoading = true;
  protected isVerified = false;
  protected errorText = 'auth.verifyEmailInvalid';
  protected tenantDisplayName = '';
  protected brandingLogoUrl: string | null = null;
  protected tenantPrimaryColor = '#2563eb';
  protected tenantSecondaryColor = '#0f172a';
  protected tenantAccentColor = '#f59e0b';

  constructor() {
    if (this.tenantSlug) {
      this.tenantBranding.getBranding(this.tenantSlug).subscribe((branding) => {
        const vars = this.tenantBranding.toCssVars(branding);
        this.tenantDisplayName = branding?.displayName ?? '';
        this.brandingLogoUrl = branding?.logoUrl ?? null;
        this.tenantPrimaryColor = vars['--tenant-primary'];
        this.tenantSecondaryColor = vars['--tenant-secondary'];
        this.tenantAccentColor = vars['--tenant-accent'];
      });
    }

    if (!this.tenantSlug || !this.token) {
      this.isLoading = false;
      this.errorText = 'auth.verifyEmailInvalid';
      return;
    }

    this.authService
      .verifyClientEmail({
        companySlug: this.tenantSlug,
        token: this.token,
      })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: () => {
          this.isVerified = true;
        },
        error: () => {
          this.errorText = 'auth.verifyEmailFailed';
        },
      });
  }
}
